import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useAccount } from '@/hooks/useAccount'
import { verifyOtp, sendOtp } from '@/lib/api'
import '@/styles/liff-login.css'
import { loginThemeVars } from '@/lib/loginTheme'
import { useFitOneLine } from '@/hooks/useFitOneLine'

const OTP_LENGTH = 6
const COUNTDOWN_SECONDS = 120

interface OtpState {
  leaseCode: string
  phoneHint: string
}

export default function OtpPage() {
  const { t } = useTranslation('auth')
  const { login } = useAuth()
  const { account, reloadAccount } = useAccount()
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as OtpState | null

  // One real input holds the whole code (see the markup), so iOS can fill it
  // from Messages and paste works natively; the six boxes only display it.
  const [code, setCode] = useState('')
  const [focused, setFocused] = useState(false)
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const titleRef = useFitOneLine(t('otp_title'))

  // Redirect if no lease code in state
  useEffect(() => {
    if (!state?.leaseCode) {
      navigate('/login')
    }
  }, [state, navigate])

  // Countdown timer
  useEffect(() => {
    if (countdown <= 0) return
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [countdown])

  // Typing, deleting, pasting and iOS's one-time-code autofill all arrive here
  // as the input's whole value.
  const handleCodeChange = (value: string) => {
    setCode(value.replace(/\D/g, '').slice(0, OTP_LENGTH))
  }

  const handleVerify = async () => {
    const otp = code
    if (otp.length !== OTP_LENGTH || !state?.leaseCode) return

    setIsLoading(true)
    setError(null)

    try {
      const tokens = await verifyOtp(state.leaseCode, otp)
      localStorage.setItem('liff_lease_code', state.leaseCode)
      await login(tokens)
      // Native shell: the lease-code login is the account resolution — pull
      // the account (name/theme/gates) now. No-op inside LINE.
      await reloadAccount()
      navigate('/', { replace: true })
    } catch {
      setError(t('otp_invalid'))
      setCode('')
      inputRef.current?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    if (!state?.leaseCode || countdown > 0) return
    try {
      await sendOtp(state.leaseCode)
      setCountdown(COUNTDOWN_SECONDS)
      setCode('')
      setError(null)
      inputRef.current?.focus()
    } catch (err: unknown) {
      // LIFF-M4: surface the throttle distinctly (mirrors LeaseCodePage).
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 429) {
        setError(t('rate_limited'))
      } else {
        setError(t('errors.something_went_wrong', { ns: 'common' }))
      }
    }
  }

  const isComplete = code.length === OTP_LENGTH
  // The box the next digit goes into, highlighted while the field has focus.
  const activeIndex = Math.min(code.length, OTP_LENGTH - 1)

  const theme = account?.liff_theme
  const panelEnabled = theme?.login_panel_enabled === 'true'
  const cssVars = loginThemeVars(theme)

  return (
    <div className="liff-login" style={cssVars}>
      <div className="liff-login__form-wrapper" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        {panelEnabled && <div className="liff-login__panel" />}
        <div className="liff-login__content">
          <button
            onClick={() => navigate('/login')}
            className="btn btn-ghost btn-sm self-start mb-4"
          >
            ← {t('actions.back', { ns: 'common' })}
          </button>

          <h1 ref={titleRef} className="liff-login__title text-base-content">{t('otp_title')}</h1>
          {state?.phoneHint && (
            <p className="text-base-content/70 mb-6">
              {t('otp_subtitle')} {state.phoneHint}
            </p>
          )}

          {/* 6-digit OTP (2026-10-02, owner): one real input carries the
              whole code with autocomplete="one-time-code", so iOS offers the
              code from Messages and fills it in one go (six one-character
              inputs could only ever take the first digit). It lies invisibly
              over six display boxes, which share the column's width instead of
              a fixed 48px each, so they never spill past the panel. Its 16px
              font stops iOS zooming in on focus. */}
          <div className="relative mb-6">
            <div className="flex gap-2 justify-center" aria-hidden="true">
              {Array.from({ length: OTP_LENGTH }, (_, i) => (
                <div
                  key={i}
                  data-testid="otp-box"
                  className={`input input-bordered flex-1 min-w-0 max-w-12 h-14 px-0 justify-center text-2xl font-bold ${focused && i === activeIndex ? 'input-primary' : ''}`}
                >
                  {code[i] ?? (focused && i === activeIndex ? <span className="w-px h-7 bg-base-content animate-pulse" /> : '')}
                </div>
              ))}
            </div>
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={OTP_LENGTH}
              value={code}
              onChange={(e) => handleCodeChange(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              aria-label={t('otp_label')}
              className="absolute inset-0 w-full h-full opacity-0 text-base caret-transparent"
            />
          </div>

          {/* Countdown */}
          <p className="text-center text-sm text-base-content/60 mb-4">
            {countdown > 0
              ? t('otp_expires', { seconds: countdown })
              : t('otp_expired')}
          </p>

          {error && (
            <div className="alert alert-error text-sm mb-4">
              <span>{error}</span>
            </div>
          )}

          <button
            className="btn w-full liff-login__btn-primary mb-3"
            onClick={() => { void handleVerify() }}
            disabled={!isComplete || isLoading}
          >
            {isLoading ? (
              <span className="loading loading-spinner loading-sm" />
            ) : (
              t('verify_otp')
            )}
          </button>

          <button
            className="btn btn-ghost w-full"
            onClick={() => { void handleResend() }}
            disabled={countdown > 0}
          >
            {t('resend_otp')}
            {countdown > 0 && ` (${countdown}s)`}
          </button>
        </div>
      </div>
    </div>
  )
}
